import React, { useEffect, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import PropTypes from 'prop-types';

import * as Layout from '@components/screen/Layout';
import SociollaCard from '@components/sociolla/SociollaCard';
import SociollaText from '@components/sociolla/SociollaText';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import SalesmanMonthPicker from './SalesmanMonthPicker';
import Colors from '@global/colors';
import { isWeb } from '@global/screens';

// Rows a page. The backend reads only this many salesmen, so it is also the read size.
export const AR_PAGE_SIZE = 10;

// Name and sales code share the one Salesman column the mockup has.
const salesmanLabel = (row) =>
  row.salesman_code
    ? `${row.salesman_name} (${row.salesman_code})`
    : row.salesman_name;

// Target is a slot so a null from a Cerebro outage renders as a dash rather than Rp 0.
// checkNullUndefinedOnly, because Table's default prints a dash for a real zero too.
const STRUCTURE = {
  salesman_name: { head: 'Salesman', body: 'salesman_label' },
  collection: {
    head: 'Collection',
    body: 'collection',
    type: 'currency',
    checkNullUndefinedOnly: true,
  },
  target: { head: 'Target AR', body: 'target', type: 'slot' },
};

const Amount = ({ value, style }) =>
  value == null ? (
    <SociollaText style={style}>-</SociollaText>
  ) : (
    <SociollaText currency style={style}>
      {value}
    </SociollaText>
  );

Amount.propTypes = {
  value: PropTypes.number,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

Amount.defaultProps = { value: null, style: null };

const SalesmanArTable = ({
  rows,
  totalData,
  skip,
  period,
  loading,
  error,
  onChangePeriod,
  onChangePage,
}) => {
  // Paged on the backend: `rows` is already the page, and its month is its own — changing it no
  // longer re-runs the cards above.
  // useApi clears its data the moment a request starts, so `rows` is [] for the whole of a page
  // or month change. Holding the last settled page lets Table dim it under a spinner instead of
  // replacing the card with one.
  const [settled, setSettled] = useState({ rows, totalData });
  useEffect(() => {
    if (!loading) {
      setSettled({ rows, totalData });
    }
  }, [loading, rows, totalData]);

  const shownRows = loading ? settled.rows : rows;
  const shownTotal = loading ? settled.totalData : totalData;

  const page = shownRows.map((row) => ({
    ...row,
    salesman_label: salesmanLabel(row),
  }));

  const body = () => {
    // Only the first load blanks the card; later reads keep their rows.
    if (loading && !shownRows.length) {
      return <Layout.Loader />;
    }
    if (error) {
      return (
        <SociollaText style={styles.unavailable}>Unavailable</SociollaText>
      );
    }
    if (!shownRows.length && !skip) {
      return (
        <SociollaText style={styles.empty}>
          No collection in this period
        </SociollaText>
      );
    }

    return (
      <>
        {isWeb ? (
          <Table
            structures={STRUCTURE}
            data={page}
            slot={{
              target: ({ data }) => (
                <Amount value={data.target} style={styles.cellText} />
              ),
            }}
            refetching={loading}
            withFetch={false}
            showSearch={false}
            showFilter={false}
            showResetFilter={false}
            bottomPaginationOnly
            hideLimitOptions
            hasNoHide
            withoutShadow
            totalData={shownTotal}
            limit={AR_PAGE_SIZE}
            skip={skip}
            onLoadMore={onChangePage}
          />
        ) : (
          // noScroll renders the rows inline so the page scrolls, not the table.
          <TableMobile
            testID="salesman-ar-table-mobile"
            tableData={page}
            renderItem={({ data, index }) => (
              <View
                style={[styles.item, index % 2 === 0 ? styles.rowAlt : null]}>
                <SociollaText type="medium" style={styles.itemName}>
                  {data.salesman_label || '-'}
                </SociollaText>
                <View style={styles.itemFigures}>
                  <Amount value={data.collection} style={styles.itemAmount} />
                  <SociollaText style={styles.itemMuted}>
                    {' '}
                    | Target AR:{' '}
                  </SociollaText>
                  <Amount value={data.target} style={styles.itemMuted} />
                </View>
              </View>
            )}
            loadingPagination={loading}
            refetching={loading}
            withFetch={false}
            showSearch={false}
            notRenderFooter
            isSmall
            noScroll
            withPagination
            totalData={shownTotal}
            skip={skip}
            limit={AR_PAGE_SIZE}
            onSetSkip={onChangePage}
          />
        )}
      </>
    );
  };

  return (
    <SociollaCard style={styles.card}>
      <SociollaText type="medium" style={styles.title}>
        AR Collection by Salesman
      </SociollaText>

      {/* Collection in the month, target as of its end; the cards above stay on the current month. */}
      <SalesmanMonthPicker
        value={period}
        disabled={loading}
        onChange={onChangePeriod}
      />

      <View style={styles.body}>{body()}</View>
    </SociollaCard>
  );
};

SalesmanArTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      salesman_id: PropTypes.string,
      salesman_name: PropTypes.string,
      salesman_code: PropTypes.string,
      collection: PropTypes.number,
      target: PropTypes.number,
    }),
  ),
  totalData: PropTypes.number,
  skip: PropTypes.number,
  period: PropTypes.shape({
    year: PropTypes.number.isRequired,
    month: PropTypes.number.isRequired,
  }).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  onChangePeriod: PropTypes.func.isRequired,
  onChangePage: PropTypes.func.isRequired,
};

SalesmanArTable.defaultProps = { rows: [], loading: false, error: false };

const styles = StyleSheet.create({
  body: { justifyContent: 'center', minHeight: 120 },
  card: { marginBottom: 0, padding: 16 },
  cellText: { color: Colors.textSecondary, fontSize: 13 },
  empty: { color: Colors.grey13, fontSize: 13, textAlign: 'center' },
  item: { paddingHorizontal: 12, paddingVertical: 12 },
  itemAmount: { color: Colors.textSecondary, fontSize: 13 },
  itemFigures: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 4 },
  itemMuted: { color: Colors.grey13, fontSize: 13 },
  itemName: { fontSize: 14 },
  rowAlt: { backgroundColor: Colors.grey12 },
  title: { fontSize: 16, marginBottom: 16 },
  unavailable: { color: Colors.red0, fontSize: 16, textAlign: 'center' },
});

export default SalesmanArTable;
